import React, { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { useNotifications } from "../hooks/notificationsContext";
import ConfirmModal from "./ConfirmModal";
import { getUserData } from "../services/userService";

export default function InboxPanel({ type = "inbox", userDisplayName, onReply }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInboxMessage, setSelectedInboxMessage] = useState(null);
  const [selectedOutboxMessage, setSelectedOutboxMessage] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, msgId: null });
  const [confirmAccept, setConfirmAccept] = useState({ open: false, msg: null });
  const [confirmWithdraw, setConfirmWithdraw] = useState({ open: false, msg: null });
  const [currentUserData, setCurrentUserData] = useState(null);

  const { addNotification } = useNotifications();
  const db = getFirestore();

  const selectedMessage = type === "inbox" ? selectedInboxMessage : selectedOutboxMessage;
  const setSelectedMessage = type === "inbox" ? setSelectedInboxMessage : setSelectedOutboxMessage;

  const theme = {
    teal: '#50E3C2',
    bg: 'rgba(13, 22, 21, 0.4)',
    rowHover: 'rgba(80, 227, 194, 0.05)',
    rowActive: 'rgba(80, 227, 194, 0.15)',
    text: '#FFF',
    muted: '#8b949e',
    font: 'JetBrains Mono, monospace'
  };

  useEffect(() => {
    const fetchUser = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return;
      const data = await getUserData(user.uid);
      setCurrentUserData(data);
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user || !userDisplayName) {
      setMessages([]);
      setLoading(false);
      return;
    }
    const uid = user.uid;
    setLoading(true);
    const messagesRef = collection(db, "Direct_Messages");
    const q = query(messagesRef, where("messageOwner", "==", uid), orderBy("timestamp", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
        const fetchedMessages = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const inboxMessages = fetchedMessages.filter(msg => msg.recipientDisplayName === userDisplayName);
        const outboxMessages = fetchedMessages.filter(msg => msg.senderDisplayName === userDisplayName);
        setMessages(type === "inbox" ? inboxMessages : outboxMessages);
        setLoading(false);
      }, (error) => {
        console.error("Failed to load messages:", error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [type, db, userDisplayName]);

  const formatTimestamp = (ts) => {
    if (!ts) return "00/00/00 00:00";
    let dateObj = ts.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(dateObj)) return "00/00/00 00:00";
    return `${String(dateObj.getUTCMonth() + 1).padStart(2, "0")}/${String(dateObj.getUTCDate()).padStart(2, "0")} ${String(dateObj.getUTCHours()).padStart(2, "0")}:${String(dateObj.getUTCMinutes()).padStart(2, "0")}`;
  };

  const handleDelete = (msgId) => setConfirmDelete({ open: true, msgId });
  const confirmDeleteMessage = async () => {
    const msgId = confirmDelete.msgId;
    setConfirmDelete({ open: false, msgId: null });
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user || !msgId) return;
    try {
      const token = await user.getIdToken();
      const response = await fetch("https://deletedirectmessage-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ docID: msgId }),
      });
      const result = await response.json();
      if (result.status === "success") {
        setMessages((prev) => prev.filter((msg) => msg.id !== msgId));
        if (selectedMessage?.id === msgId) setSelectedMessage(null);
        addNotification("Message deleted");
      }
    } catch (err) { addNotification("Error deleting message"); }
  };

  const confirmAcceptEmployee = async () => {
    if (!confirmAccept.msg) return;
    const msg = confirmAccept.msg;
    setConfirmAccept({ open: false, msg: null });
    try {
      const token = await getAuth().currentUser.getIdToken();
      const response = await fetch("https://acceptapplication-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ companyTicker: msg.companyTicker, applicantDisplayName: msg.senderDisplayName, messageDocID: msg.id }),
      });
      const result = await response.json();
      if (result.status === "success" || result.success) {
        addNotification(`Accepted ${msg.senderDisplayName}!`);
        setMessages((prev) => prev.filter((m) => m.id !== msg.id));
      }
    } catch (err) { addNotification("Error accepting employee"); }
  };

  const confirmWithdrawApplication = async () => {
    if (!confirmWithdraw.msg) return;
    const msg = confirmWithdraw.msg;
    setConfirmWithdraw({ open: false, msg: null });
    try {
      const token = await getAuth().currentUser.getIdToken();
      const response = await fetch("https://companycancelapplication-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ companyTicker: msg.companyTicker }),
      });
      const result = await response.json();
      if (response.ok) {
        setMessages((prev) => prev.filter((m) => m.id !== msg.id));
        if (selectedMessage?.id === msg.id) setSelectedMessage(null);
        addNotification(result.message || "Withdrawn");
      }
    } catch (err) { addNotification("Error withdrawing"); }
  };

const handleCastVote = async (msg, vote) => {
    try {
      const token = await getAuth().currentUser.getIdToken();

      const ticker = msg.companyTicker || msg.ticker || "UNKNOWN";

      const response = await fetch("https://castvote-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          ticker: ticker,
          vote: vote
        }),
      });

if (response.ok) {
  const displayTicker = ticker || "SYS";
  const displayVote = (vote || "NUL").toUpperCase();
  
  addNotification(`Vote recorded: ${displayTicker} - ${displayVote}`);
}
    } catch (err) { 
      console.error("Vote Error:", err);
      addNotification(`Error: vote rejected`); 
    }
  };

  return (
    <div className="inbox-panel" style={{ 
      color: theme.text, 
      fontFamily: theme.font, 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      overflow: 'hidden'
    }}>
      {loading ? (
        <div style={{ padding: '20px', color: theme.teal }}>LOADING...</div>
      ) : (
        <>
          {/* SELECTED MESSAGE PREVIEW */}
          {selectedMessage && (
            <div style={{ 
              padding: '15px', 
              background: 'rgba(80, 227, 194, 0.08)', 
              borderBottom: `1px solid ${theme.teal}`,
              marginBottom: '10px',
              flexShrink: 0
            }}>
              <div style={{ fontSize: '0.6rem', color: theme.teal, marginBottom: '5px' }}>MESSAGE</div>
              <p style={{ fontSize: '0.8rem', margin: 0, whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
                {selectedMessage.message}
              </p>
            </div>
          )}

          {/* MESSAGES LIST */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 5px' }}>
            {messages.length === 0 ? (
              <p style={{ padding: '20px', fontSize: '0.8rem', color: theme.muted }}>NO MESSAGES FOUND</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, background: '#0d1615', zIndex: 10 }}>
                  <tr>
                    <th style={{ textAlign: 'left', fontSize: '0.65rem', color: theme.teal, padding: '8px' }}>{type === "inbox" ? "SENDER" : "RECIPIENT"}</th>
                    <th style={{ textAlign: 'left', fontSize: '0.65rem', color: theme.teal, padding: '8px' }}>TIMESTAMP</th>
                    <th style={{ textAlign: 'right', fontSize: '0.65rem', color: theme.teal, padding: '8px' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((msg) => (
                    <tr
                      key={msg.id}
                      onClick={() => setSelectedMessage(msg)}
                      style={{
                        cursor: 'pointer',
                        borderBottom: '1px solid rgba(80, 227, 194, 0.05)',
                        background: selectedMessage?.id === msg.id ? theme.rowActive : 'transparent',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = theme.rowHover}
                      onMouseLeave={(e) => e.currentTarget.style.background = selectedMessage?.id === msg.id ? theme.rowActive : 'transparent'}
                    >
                      <td style={{ padding: '10px 8px', fontSize: '0.75rem' }}>
                        {type === "inbox" ? msg.senderDisplayName : msg.recipientDisplayName}
                      </td>
                      <td style={{ padding: '10px 8px', fontSize: '0.7rem', color: theme.muted }}>
                        {formatTimestamp(msg.timestamp)}
                      </td>
                     <td style={{ padding: '10px 8px', textAlign: 'right' }}>
  <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
    
    {/* REPLY BUTTON */}
    <button
      className="jetbrains-btn"
      style={{ 
        padding: '2px 8px', 
        fontSize: '0.55rem', 
        background: 'rgba(80, 227, 194, 0.1)', 
        color: theme.teal, 
        border: '1px solid rgba(80, 227, 194, 0.3)',
        cursor: 'pointer'
      }}
      onClick={(e) => { e.stopPropagation(); onReply?.(msg); }}
    >
      REPLY
    </button>
    
    {/* DELETE BUTTON */}
    <button
      className="jetbrains-btn"
      style={{ 
        padding: '2px 8px', 
        fontSize: '0.55rem', 
        background: 'rgba(255, 85, 85, 0.1)', 
        color: '#ff5555', 
        border: '1px solid rgba(255, 85, 85, 0.3)',
        cursor: 'pointer'
      }}
      onClick={(e) => { e.stopPropagation(); handleDelete(msg.id); }}
    >
      DELETE
    </button>

{msg.senderDisplayName?.trim() === "Vote" && (
  <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto', paddingLeft: '10px' }}>
    <button 
      className="jetbrains-btn" 
      style={{ 
        padding: '2px 8px', 
        flexShrink: 0, 
        fontSize: '0.65rem', 
        background: 'rgba(80, 227, 194, 0.1)', 
        color: theme.teal, 
        border: '1px solid rgba(80, 227, 194, 0.3)',
        cursor: 'pointer'
      }}
      onClick={(e) => { e.stopPropagation(); handleCastVote(msg, "yea"); }}
    >
      YEA
    </button>
    <button 
      className="jetbrains-btn" 
      style={{ 
        padding: '2px 8px', 
        flexShrink: 0, 
        fontSize: '0.65rem', 
        background: 'rgba(255, 85, 85, 0.1)', 
        color: '#ff5555', 
        border: '1px solid rgba(255, 85, 85, 0.3)',
        cursor: 'pointer'
      }}
      onClick={(e) => { e.stopPropagation(); handleCastVote(msg, "nay"); }}
    >
      NAY
    </button>
  </div>
)}

    {/* ACCEPT BUTTON */}
    {msg.companyApplication && currentUserData?.userCEO && (
      <button 
        className="jetbrains-btn" 
        style={{ padding: '2px 8px', fontSize: '0.55rem', background: 'rgba(80, 227, 194, 0.1)', color: theme.teal, border: '1px solid rgba(80, 227, 194, 0.3)' }}
        onClick={(e) => { e.stopPropagation(); setConfirmAccept({ open: true, msg }); }}
      >
        ACCEPT
      </button>
    )}

    {/* WITHDRAW BUTTON - THEMED ORANGE */}
    {msg.senderDisplayName === "Application" && msg.recipientDisplayName === userDisplayName && (
      <button 
        className="jetbrains-btn" 
        style={{ 
          padding: '2px 8px', 
          fontSize: '0.55rem', 
          background: 'rgba(255, 184, 108, 0.1)', 
          color: '#ffb86c', 
          border: '1px solid rgba(255, 184, 108, 0.3)' 
        }} 
        onClick={(e) => { e.stopPropagation(); setConfirmWithdraw({ open: true, msg }); }}
      >
        WITHDRAW
      </button>
    )}
  </div>
</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* MODALS (logic remains same) */}
          {confirmDelete.open && (
            <ConfirmModal title="DELETE MESSAGE" message="Confirm deletion of record?" confirmText="DELETE" onConfirm={confirmDeleteMessage} onCancel={() => setConfirmDelete({ open: false, msgId: null })} />
          )}
          {confirmAccept.open && (
            <ConfirmModal title="ACCEPT USER" message={`Accept ${confirmAccept.msg.senderDisplayName}?`} confirmText="ACCEPT" onConfirm={confirmAcceptEmployee} onCancel={() => setConfirmAccept({ open: false, msg: null })} />
          )}
          {confirmWithdraw.open && (
            <ConfirmModal title="WITHDRAW APPLICATION" message={`Cancel application to ${confirmWithdraw.msg.companyTicker}?`} confirmText="WITHDRAW" onConfirm={confirmWithdrawApplication} onCancel={() => setConfirmWithdraw({ open: false, msg: null })} />
          )}
        </>
      )}
    </div>
  );
}